import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function useTableView(entity, defaultColumns = []) {
    const { getAuthHeader } = useAuth();
    const [views, setViews] = useState([]);
    const [activeViewId, setActiveViewId] = useState(null);
    const [visibleColumnKeys, setVisibleColumnKeys] = useState(defaultColumns);
    const [columnSizing, setColumnSizing] = useState({});
    const [sorting, setSorting] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const saveTimer = useRef(null);

    // Load views on mount
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/table-views`, {
                    headers: getAuthHeader(),
                    params: { entity },
                });
                const data = res.data || [];
                setViews(data);

                // Prefer the user's own default, then any default, then the
                // first view. #534: the list is sorted by updated_at, so an
                // org template (which can carry is_default from the admin's
                // personal view it was pushed from) edited after the user's
                // auto-saved "My view" would otherwise win — dropping the
                // user's customisation and forking a second "My view" on
                // their next edit.
                const defaultView = data.find(v => v.is_default && v.scope === 'personal')
                    || data.find(v => v.is_default)
                    || data[0];
                if (defaultView) {
                    applyView(defaultView);
                } else {
                    // No views — use default columns
                    setVisibleColumnKeys(defaultColumns);
                }
            } catch { /* keep defaults */ }
            finally {
                // #534: whatever happened while the view list was in flight is
                // discarded here — the loaded set is the clean baseline, and
                // isDirty must not carry pre-load churn into auto-save.
                setIsDirty(false);
                setLoaded(true);
            }
        };
        load();
    }, [entity, getAuthHeader]); // eslint-disable-line

    const applyView = (view) => {
        setActiveViewId(view.view_id);
        const cols = (view.columns || [])
            .filter(c => c.visible !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(c => c.field_key);
        setVisibleColumnKeys(cols.length > 0 ? cols : defaultColumns);

        // Apply column widths
        const sizing = {};
        (view.columns || []).forEach(c => {
            if (c.width) sizing[c.field_key] = c.width;
        });
        setColumnSizing(sizing);

        // Apply sorting
        if (view.sort_by) {
            setSorting([{ id: view.sort_by, desc: view.sort_direction === 'desc' }]);
        } else {
            setSorting([]);
        }
        setIsDirty(false);
    };

    const switchView = useCallback((viewId) => {
        const view = views.find(v => v.view_id === viewId);
        if (view) applyView(view);
    }, [views]); // eslint-disable-line

    const buildColumnsPayload = useCallback(() => {
        return visibleColumnKeys.map((key, idx) => ({
            field_key: key,
            visible: true,
            order: idx,
            width: columnSizing[key] || null,
        }));
    }, [visibleColumnKeys, columnSizing]);

    // #534 audit: one payload builder for the three view-write
    // endpoints — the two POST paths used to build it independently
    // (finding F06), one typo away from drifting apart.
    const buildViewPayload = useCallback((extra = {}) => ({
        entity,
        columns: buildColumnsPayload(),
        sort_by: sorting[0]?.id || null,
        sort_direction: sorting[0]?.desc ? 'desc' : 'asc',
        ...extra,
    }), [entity, buildColumnsPayload, sorting]);

    const saveView = useCallback(async () => {
        if (!activeViewId) return;
        const gen = dirtyGenRef.current;
        try {
            const { columns, sort_by, sort_direction } = buildViewPayload();
            const payload = { columns, sort_by, sort_direction };
            await axios.put(`${API}/api/table-views/${activeViewId}`, payload, { headers: getAuthHeader() });
            if (dirtyGenRef.current === gen) setIsDirty(false);
        } catch { /* ignore */ }
    }, [activeViewId, buildViewPayload, getAuthHeader]);

    const saveAsView = useCallback(async (name) => {
        const gen = dirtyGenRef.current;
        try {
            const payload = buildViewPayload({ name });
            const res = await axios.post(`${API}/api/table-views`, payload, { headers: getAuthHeader() });
            setViews(prev => [res.data, ...prev]);
            setActiveViewId(res.data.view_id);
            if (dirtyGenRef.current === gen) setIsDirty(false);
        } catch { /* ignore */ }
    }, [buildViewPayload, getAuthHeader]);

    // ── #534: auto-persist column changes ──────────────────────────────────
    // Customising a table must survive navigation and sessions (the original
    // report: a manually added Owner column disappeared on navigation). The
    // manual Save button stays, but edits now also persist on their own: an
    // editable personal view saves (debounced), and a user without one gets a
    // personal view created from their current columns — including
    // org-template users, whose PUT on the template would be rejected.
    const activeViewRef = useRef(null);
    activeViewRef.current = views.find(v => v.view_id === activeViewId) || null;
    const pendingFlushRef = useRef(null);
    // #534 audit: monotonic dirty generation. Every edit bumps it; a save
    // captures the generation it set out with and only clears isDirty when
    // nothing changed since — a response from an older save must not mark
    // a newer, still-unsaved edit clean (and cancel its scheduled save).
    const dirtyGenRef = useRef(0);
    const creatingViewRef = useRef(false);

    const markDirty = useCallback(() => {
        dirtyGenRef.current += 1;
        setIsDirty(true);
    }, []);

    const ensurePersonalView = useCallback(async () => {
        if (creatingViewRef.current) return; // a creation POST is already in flight
        creatingViewRef.current = true;
        const gen = dirtyGenRef.current;
        try {
            const res = await axios.post(
                `${API}/api/table-views`,
                buildViewPayload({ name: 'My view', is_default: true }),
                { headers: getAuthHeader() },
            );
            setViews(prev => [res.data, ...prev]);
            setActiveViewId(res.data.view_id);
            if (dirtyGenRef.current === gen) setIsDirty(false);
        } catch { /* keep local state */ }
        finally { creatingViewRef.current = false; }
    }, [buildViewPayload, getAuthHeader]);

    useEffect(() => {
        if (!loaded || !isDirty) { pendingFlushRef.current = null; return undefined; }
        const activeView = activeViewRef.current;
        const canEditPersonal = activeViewId && activeView && activeView.scope === 'personal';
        const persist = canEditPersonal ? saveView : ensurePersonalView;
        pendingFlushRef.current = persist;
        saveTimer.current = setTimeout(persist, 800);
        return () => { clearTimeout(saveTimer.current); };
    }, [loaded, isDirty, activeViewId, saveView, ensurePersonalView]);

    // Flush the pending change on unmount — navigating away right after
    // customising must not lose it (the original #534 report).
    useEffect(() => () => {
        clearTimeout(saveTimer.current);
        if (pendingFlushRef.current) {
            const flush = pendingFlushRef.current;
            pendingFlushRef.current = null;
            flush();
        }
    }, []);

    const deleteView = useCallback(async (viewId) => {
        try {
            await axios.delete(`${API}/api/table-views/${viewId}`, { headers: getAuthHeader() });
            setViews(prev => prev.filter(v => v.view_id !== viewId));
            if (activeViewId === viewId) {
                setActiveViewId(null);
                setVisibleColumnKeys(defaultColumns);
            }
        } catch { /* ignore */ }
    }, [activeViewId, defaultColumns, getAuthHeader]);

    const pushToOrg = useCallback(async (viewId) => {
        try {
            await axios.post(`${API}/api/table-views/${viewId}/push`, {}, { headers: getAuthHeader() });
            setViews(prev => prev.map(v => v.view_id === viewId ? { ...v, scope: 'org', is_template: true } : v));
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    // Mark dirty on column changes
    const updateColumns = useCallback((newKeys) => {
        setVisibleColumnKeys(newKeys);
        markDirty();
    }, [markDirty]);

    const addColumn = useCallback((key) => {
        setVisibleColumnKeys(prev => [...prev, key]);
        markDirty();
    }, [markDirty]);

    const removeColumn = useCallback((key) => {
        setVisibleColumnKeys(prev => prev.filter(k => k !== key));
        // A hidden column can't show its sort arrow, so it stops sorting the list too (#1000).
        setSorting(prev => prev.filter(s => s.id !== key));
        markDirty();
    }, [markDirty]);

    const handleSortingChange = useCallback((updater) => {
        setSorting(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            markDirty();
            return next;
        });
    }, [markDirty]);

    const handleColumnSizingChange = useCallback((updater) => {
        setColumnSizing(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            markDirty();
            return next;
        });
    }, [markDirty]);

    return {
        views,
        activeViewId,
        visibleColumnKeys,
        columnSizing,
        sorting,
        isDirty,
        loaded,
        switchView,
        saveView,
        saveAsView,
        deleteView,
        pushToOrg,
        addColumn,
        removeColumn,
        updateColumns,
        setSorting: handleSortingChange,
        setColumnSizing: handleColumnSizingChange,
    };
}
